import dayjs from 'dayjs';

import type { StoreHit } from '@/lib/algolia/stores';
import { getWeekdayFromDate, type Weekday } from '@/lib/const';

export type TimeInterval = { startTime: string; endTime: string };

export type Schedule = Partial<Record<Weekday, TimeInterval[]>> | null;

export type HolidayHour = {
  date: string;
  open_intervals: Array<{ start: string; end: string }> | null;
};

export type TimeSlotOption = {
  label: string;
  value: string;
};

function mapTimes(slots: TimeInterval[] | undefined): TimeSlotOption[] {
  return (
    slots?.map((item) => ({
      label: `${item.startTime} - ${item.endTime}`,
      value: item.startTime,
    })) ?? []
  );
}

function generateHourlyIntervals(
  holidayData: HolidayHour[],
  regularAppointments: Schedule,
  date: string | Date,
  reopenDate?: string
): { intervals: TimeInterval[]; isDisabled: boolean } {
  const result: TimeInterval[] = [];

  if (reopenDate) {
    const reopenDateTime = new Date(reopenDate).getTime();
    const currentDateTime = new Date(date).getTime();
    if (currentDateTime < reopenDateTime) {
      return { intervals: [], isDisabled: true };
    }
  }

  const formattedDate = dayjs(date).format('YYYY-MM-DD');
  const holidayDateInfo = holidayData.find((item) => item.date === formattedDate);

  if (holidayDateInfo) {
    if (!holidayDateInfo.open_intervals) {
      return { intervals: [], isDisabled: true };
    }

    holidayDateInfo.open_intervals.forEach((interval) => {
      const [startHour, startMin] = interval.start.split(':').map(Number);
      const [endHour, endMin] = interval.end.split(':').map(Number);
      const startTotalMinutes = startHour * 60 + startMin;
      const endTotalMinutes = endHour * 60 + endMin;

      for (let minutes = startTotalMinutes; minutes < endTotalMinutes; minutes += 60) {
        const slotStartHour = Math.floor(minutes / 60);
        const slotStartMin = minutes % 60;
        const slotEndMinutes = Math.min(minutes + 60, endTotalMinutes);
        const slotEndHour = Math.floor(slotEndMinutes / 60);
        const slotEndMin = slotEndMinutes % 60;

        result.push({
          startTime: `${String(slotStartHour).padStart(2, '0')}:${String(slotStartMin).padStart(2, '0')}`,
          endTime: `${String(slotEndHour).padStart(2, '0')}:${String(slotEndMin).padStart(2, '0')}`,
        });
      }
    });

    return { intervals: result, isDisabled: false };
  }

  const dayOfWeek = getWeekdayFromDate(new Date(date));
  const regularDayAppointments = regularAppointments?.[dayOfWeek];

  if (regularDayAppointments?.length) {
    regularDayAppointments.forEach((interval) => {
      result.push(interval);
    });
    return { intervals: result, isDisabled: false };
  }

  return { intervals: [], isDisabled: true };
}

/**
 * Resolve unavailable weekdays from store hours and empty appointment slots.
 */
export function resolveUnavailableAppointmentWeekdays(store?: StoreHit): Weekday[] {
  if (!store) return [];

  const closed = Object.entries(store.hours ?? {})
    .filter(([, daySchedule]) => {
      const schedule = daySchedule as { isClosed?: boolean; openIntervals?: unknown[] };
      if (schedule.isClosed) return true;
      return !schedule.openIntervals?.length;
    })
    .map(([day]) => day as Weekday);

  const appointments = store.appointments as Schedule | undefined;
  const noSlots = Object.entries(appointments ?? {})
    .filter(([, slots]) => !Array.isArray(slots) || slots.length === 0)
    .map(([day]) => day as Weekday);

  return Array.from(new Set([...closed, ...noSlots]));
}

/**
 * Build time slot options and non-working holiday dates for a selected day.
 */
export function getAvailableTimeSlots(
  appointments: Schedule,
  date: string | Date,
  holidayHours?: HolidayHour[],
  reopenDate?: string
): { times: TimeSlotOption[]; nonWorkingDays: string[] } {
  const nonWorkingDays: string[] = [];
  const { intervals, isDisabled } = generateHourlyIntervals(
    holidayHours ?? [],
    appointments,
    date,
    reopenDate
  );

  (holidayHours ?? []).forEach((holiday) => {
    if (holiday.open_intervals === null) {
      nonWorkingDays.push(holiday.date);
    }
  });

  if (isDisabled) {
    return { times: [], nonWorkingDays };
  }

  return { times: mapTimes(intervals), nonWorkingDays };
}

/**
 * Check whether a calendar date should be disabled for booking.
 */
export function isDateDisabled(
  date: Date,
  unavailableAppointmentWeekdays: Weekday[],
  nonWorkingDays: string[]
): boolean {
  const formattedDate = dayjs(date).format('YYYY-MM-DD');
  const dayName = getWeekdayFromDate(date);

  if (nonWorkingDays.includes(formattedDate)) return true;
  if (unavailableAppointmentWeekdays.includes(dayName)) return true;

  return false;
}

/**
 * Find the next bookable date within the allowed window.
 */
export function getNextAvailableDate(start: Date, unavailable?: Weekday[], maxDays = 29): string {
  let candidate = dayjs(start);

  for (let i = 0; i < maxDays; i++) {
    const weekday = candidate.format('dddd').toLowerCase() as Weekday;
    if (!unavailable?.includes(weekday)) {
      return candidate.format('YYYY-MM-DD');
    }
    candidate = candidate.add(1, 'day');
  }

  return '';
}
